import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { FC } from 'react';
import { ICandidate, IMappingFormSettingsResponse } from 'wisestep-api';

import ISubmissionSettingContext from '../../types/ISubmissionSettingContext';
import ISubmissionSettingsResponse from '../../types/ISubmissionSettingsResponse';
import WsColors from '../../WsColors';
import { Item } from '../Item';
import { FileTypeVariants, WsFileUpload } from '../WsFileUpload';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

interface IDocumentInfo {
  candidate?: ICandidate | null;
  submissionSetting?: ISubmissionSettingsResponse | ISubmissionSettingContext | null;
  onUpload: (args: File[]) => void;
  resumeAction?: (args1: string, args2: string | null, args3?: string | null) => void;
  purpose?: 'submission' | 'mapping';
  mappingFormSettings?: IMappingFormSettingsResponse;
}

const DocumentInfo: FC<IDocumentInfo> = ({
  candidate,
  submissionSetting,
  onUpload,
  resumeAction,
  purpose = 'submission',
  mappingFormSettings,
}) => (
  <>
    {purpose === 'submission' && !submissionSetting?.otherDocuments?.hidden && (
      <GridItem>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
          {`Documents 
          ${submissionSetting?.otherDocuments?.required ? '*' : ''}`}
        </WsTypography>
        {candidate?.otherDocuments?.map((document, index: number) => (
          <Grid key={index} container columnSpacing={1} px={0.5}>
            <Grid item xs={7}>
              <WsTypography variant={WsTypoVariants.BODY1}>
                {document?.originalName ?? ''}
              </WsTypography>
            </Grid>
            <Grid container item xs={3.5} columnSpacing={2}>
              <Grid item>
                <FileDownloadOutlinedIcon
                  onClick={() =>
                    resumeAction &&
                    resumeAction(
                      'download',
                      document.documentLocation,
                      document?.originalName ?? ''
                    )
                  }
                  sx={{ color: WsColors.TheBlues500, fontSize: 24, cursor: 'pointer' }}
                />
              </Grid>
              <Grid item>
                <VisibilityOutlinedIcon
                  onClick={() => resumeAction && resumeAction('view', document.documentLocation)}
                  sx={{ color: WsColors.TheBlues500, fontSize: 24, cursor: 'pointer' }}
                />
              </Grid>
              <Grid item>
                <CloseIcon
                  onClick={() =>
                    resumeAction && resumeAction('removeOtherDocument', document.documentLocation)
                  }
                  sx={{ color: WsColors.Red500, fontSize: 24, cursor: 'pointer' }}
                />
              </Grid>
            </Grid>
          </Grid>
        ))}
        <WsFileUpload
          multiple={false}
          onUpload={(file: File[]) => onUpload(file)}
          name={'document'}
          placeholderText="Drag & drop .pdf or .docx or .png file "
          buttonText={'Upload File'}
          maxFileSize={3}
          fileTypes={FileTypeVariants.DOCS_AND_IMAGES}
        />
      </GridItem>
    )}
    {purpose === 'mapping' && mappingFormSettings?.otherDocuments.show && (
      <GridItem>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
          {'Documents' + (mappingFormSettings.otherDocuments.mandatory ? '*' : '')}
        </WsTypography>
        {candidate?.otherDocuments?.map((document, index: number) => (
          <Stack key={index} direction="row" spacing={2} mb={2}>
            <WsTypography variant={WsTypoVariants.BODY1}>
              {document?.originalName ?? ''}
            </WsTypography>
            <DownloadIcon
              onClick={() => resumeAction && resumeAction('download', document.documentLocation)}
              sx={{ color: WsColors.TheBlues500, fontSize: '24px', cursor: 'pointer' }}
            />
            <VisibilityIcon
              onClick={() => resumeAction && resumeAction('view', document.documentLocation)}
              sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
            />
            <CloseIcon
              onClick={() =>
                resumeAction && resumeAction('removeOtherDocument', document.documentLocation)
              }
              sx={{ color: WsColors.Red500, cursor: 'pointer' }}
            />
          </Stack>
        ))}
        <WsFileUpload
          multiple={true}
          onUpload={(file: File[]) => onUpload(file)}
          name={'document'}
          placeholderText="Drag & drop .pdf or .docx file "
          buttonText={'Upload File'}
          maxFileSize={3}
        />
      </GridItem>
    )}
  </>
);

export default DocumentInfo;
